<script setup lang="ts">
import { type Component } from 'vue'
import { Briefcase, FilePlus2 } from 'lucide-vue-next'

const actions: { title: string; description: string; button: string; icon: Component; route: string }[] = [
  {
    title: 'Set Up a Fund',
    description: 'Create your first fund vehicle, define terms, and start accepting commitments.',
    button: 'Create Fund',
    icon: Briefcase,
    route: 'investing.fund-new',
  },
  {
    title: 'Set Up an SPV',
    description: 'Launch a single-deal SPV to pool capital for a specific investment opportunity.',
    button: 'Create SPV',
    icon: FilePlus2,
    route: 'investing.spv-new',
  },
]
</script>

<template>
  <div
    class="mx-auto flex min-h-[calc(100vh-100px)] max-w-[540px] flex-col items-center justify-center px-6 py-10 text-center"
  >
    <div class="icon-wrap relative mb-9 h-[160px] w-[160px]">
      <div class="ring-outer absolute inset-0 rounded-full border-[1.5px] border-[rgba(0,132,180,0.08)]" />
      <div class="ring-middle absolute inset-[32px] rounded-full border-[1.5px] border-[rgba(0,145,146,0.15)]" />
      <div class="ring-inner absolute inset-[48px] rounded-full border-[1.5px] border-[rgba(0,145,146,0.28)]" />
      <div
        class="check-circle absolute inset-[56px] flex items-center justify-center rounded-full shadow-[0_6px_24px_rgba(0,145,146,0.3)]"
      >
        <svg
          width="22"
          height="22"
          fill="none"
          stroke="white"
          stroke-width="2.5"
          viewBox="0 0 24 24"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    </div>

    <p class="eyebrow mb-3 text-[13px] font-semibold uppercase tracking-[0.1em] text-[#009192]">You're all set</p>
    <h2 class="title mb-4 text-[32px] font-bold leading-[1.2] tracking-tight text-gray-900">
      Your firm is ready to <span class="gradient-text">go</span>
    </h2>

    <!-- Action Cards -->
    <div class="cards mb-10 mt-10 grid w-full grid-cols-2 gap-3">
      <RouterLink
        v-for="(action, i) in actions"
        :key="action.title"
        :to="{ name: action.route }"
        class="action-card group flex flex-col items-center rounded-2xl border border-gray-200 bg-white px-7 py-10 text-center no-underline transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:border-[#009192]/30 hover:shadow-[0_4px_20px_rgba(0,145,146,0.1)]"
        :style="{ animationDelay: `${0.75 + i * 0.08}s` }"
      >
        <div
          class="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-[rgba(0,145,146,0.12)] bg-[linear-gradient(135deg,rgba(0,145,146,0.08),rgba(0,132,180,0.08))] text-[#009192]"
        >
          <component :is="action.icon" :size="20" :stroke-width="1.5" />
        </div>
        <h3 class="mb-2 text-[16px] font-bold text-gray-900">{{ action.title }}</h3>
        <p class="mb-4 text-[13px] leading-[1.6] text-gray-500">{{ action.description }}</p>
        <span
          class="mt-auto rounded-lg bg-[#009192]/[0.08] px-5 py-2 text-[13px] font-semibold text-[#009192] transition-colors duration-150 group-hover:bg-[#009192] group-hover:text-white"
        >
          {{ action.button }}
        </span>
      </RouterLink>
    </div>

    <RouterLink
      :to="{ name: 'dashboard' }"
      class="cta inline-flex items-center gap-2.5 rounded-xl border-none px-8 py-3.5 font-[inherit] text-sm font-semibold text-white no-underline shadow-[0_4px_20px_rgba(0,145,146,0.2)] transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:shadow-[0_6px_28px_rgba(0,145,146,0.35)] active:scale-[0.97]"
    >
      Go to Dashboard
      <svg
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
        stroke-linecap="round"
        stroke-linejoin="round"
        width="16"
        height="16"
      >
        <path d="M5 12h14m-6-6l6 6-6 6" />
      </svg>
    </RouterLink>
  </div>
</template>

<style scoped>
.check-circle {
  background: linear-gradient(135deg, #009192, #0084b4);
}

.gradient-text {
  background: linear-gradient(90deg, #009192, #0084b4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.cta {
  background: linear-gradient(135deg, #009192, #0084b4);
}

.ring-outer {
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

.ring-middle {
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.18s both;
}

.ring-inner {
  animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.26s both;
}

.check-circle {
  animation: scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.35s both;
}

.eyebrow {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.45s both;
}

.title {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.55s both;
}

.action-card {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.cta {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) 1.1s both;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ring-outer,
  .ring-middle,
  .ring-inner,
  .check-circle,
  .eyebrow,
  .title,
  .action-card,
  .cta {
    animation: none;
  }

  .cta {
    transition: none;
  }
}
</style>
